import './PlayerContainer.css';
import React from 'react';
import Player from './Player';
import GenericPlayerMessage from './GenericPlayerMessage';
import UnsupportedPlaybackMessage from './UnsupportedPlaybackMessage';
import { kalturaServices } from '../services/kaltura';
import { getUserAgent, userAgentContains, getUrlParameter } from '../helpers';

const MESSAGE_MAP = {
  NO_MEDIA_ID: 'No Media ID provided',
  INVALID_MEDIA_ID: 'Invalid Media ID provided',
};

class PlayerContainer extends React.Component {
  state = {
    mediaInfo: {},
    playbackSupported: null,
    loaded: false,
  };

  componentDidMount() {
    if (this.props.mediaID) {
      this.setUpMedia();
    } else {
      this.setState({ loaded: true });
    }
  }

  setUpMedia = async () => {
    if (this.props.mediaID) {
      const response = await kalturaServices.getMediaInfo(
        this.props.ks,
        this.props.mediaID
      );
      const { result } = response.data;
      if (result && !result.error) {
        if (this.isPlaybackSupportedOnBrowser(result)) {
          this.setState({
            mediaInfo: result,
            playbackSupported: true,
          });
        } else {
          this.setState({
            playbackSupported: false,
            mediaInfo: {
              ...result,
              displayTitle: this.getMediaTitle(result),
            },
          });
        }
      }
      this.setState({ loaded: true });
    }
  };

  getStreamType = () => {
    let streamType = '';
    const userAgent = getUserAgent();
    if (
      this.state.mediaInfo.type === process.env.REACT_APP_LINEAR_MEDIA_TYPE_ID
    ) {
      // LINEAR
      if (this.isEncryptedMedia(this.state.mediaInfo)) {
        // Encrypted stream
        if (userAgent.browser.name === 'Safari') {
          streamType = 'HLS_Web';
        } else {
          streamType = 'DASH_Web';
        }
      } else {
        // Clear stream
        if (userAgent.os.name === 'Linux') {
          streamType = 'DASH_Web';
        } else {
          streamType = 'HLS_Web';
        }
      }
    } else {
      // VOD
      if (this.is360Media(this.state.mediaInfo)) {
        //   360 VOD or if clear VOD in mobile browsers
        streamType = 'HLS_360';
      } else if (userAgent.isMobile) {
        streamType = 'HLS_Web';
      } else {
        // Regular VOD on desktop browsers
        switch (userAgent.browser.name) {
          case 'Edge':
          case 'Chrome':
            streamType = 'DASH_Web';
            break;

          case 'IE':
            if (userAgent.os.version <= 6.1) {
              // IE on Windows 7 and below
              streamType = 'SS_Web';
            } else {
              // IE on Windows 10/8.1
              if (this.isEncryptedMedia(this.state.mediaInfo)) {
                streamType = 'SS_Web';
              } else {
                streamType = 'HLS_Web';
              }
            }
            break;

          case 'Firefox':
            if (parseInt(userAgent.browser.version) >= 46) {
              // Firefox v46 and above
              streamType = 'DASH_Web';
            } else {
              // Firefox v45 and below
              streamType = 'SS_Web';
            }
            break;

          case 'Safari':
            streamType = 'HLS_Web';
            break;

          default:
            streamType = 'DASH_Web';
            break;
        }
      }
    }
    return streamType;
  };

  getFileID = () => {
    let streamType = '';

    const streamTypeFromParams = getUrlParameter('stream');
    if (streamTypeFromParams) {
      streamType = streamTypeFromParams;
    } else {
      streamType = this.getStreamType();
    }
    console.log('Selecting streamType:', streamType);
    // Select media file based on stream type
    const { mediaFiles } = this.state.mediaInfo;
    if (!mediaFiles) return 0;
    const mediaFile = mediaFiles.find(
      (file) =>
        typeof file.type === 'string' &&
        file.type.toUpperCase() === streamType.toUpperCase()
    );
    console.log('KalturaMediaFile Object', mediaFile);
    return mediaFile ? mediaFile.id : 0;
  };

  getMediaTitle(mediaInfo) {
    const isEpisodic = [
      process.env.REACT_APP_EPISODE_MEDIA_TYPE_ID,
      process.env.REACT_APP_NEWS_EPISODE_MEDIA_TYPE_ID,
    ].includes(String(mediaInfo.type));

    let { name, metas } = mediaInfo;
    let mediaTitle = name;

    const { 'Episode name': epiName, 'Episode number': epiNum } = metas;
    if (isEpisodic) {
      if (epiNum) {
        mediaTitle += ': EP ' + epiNum.value;
      }
      if (epiName) {
        mediaTitle += ' - ' + epiName.value;
      }
    }
    return mediaTitle;
  }

  is360Media(mediaInfo) {
    const { 'Media Sub Type': mediaSubType } = mediaInfo.metas;
    const { value } = mediaSubType;

    if (value && value.indexOf('360') > -1) {
      return true;
    }
    return false;
  }

  isEncryptedMedia(mediaInfo) {
    if (String(mediaInfo.type) === process.env.REACT_APP_LINEAR_MEDIA_TYPE_ID) {
      // LIVE
      const { category } = mediaInfo.tags.Category.objects;
      if (category) {
        const udrmCategory = category.find(
          (meta) => meta.value === 'uDRM_Live'
        );
        if (udrmCategory) return true;
      }
    } else {
      // VOD
      const { value: encryptionVal } = mediaInfo.metas.Encryption;
      return encryptionVal || false;
    }
    return false;
  }

  isPlaybackSupportedOnBrowser(mediaInfo) {
    const userAgent = getUserAgent();

    // Encrypted Content: IE Check
    if (this.isEncryptedMedia(mediaInfo)) {
      if (userAgent.browser.name === 'IE') {
        return false;
      }
    }

    // 360 VIDEOS
    if (this.is360Media(mediaInfo)) {
      if (userAgentContains('FBIOS') || userAgentContains('iPhone')) {
        return false;
      }
    }

    return true;
  }

  render() {
    const renderedContent = () => {
      if (!this.props.mediaID) {
        // No Media ID
        return <GenericPlayerMessage message={MESSAGE_MAP.NO_MEDIA_ID} />;
      } else {
        if (this.state.mediaInfo && this.state.mediaInfo.id) {
          if (this.state.playbackSupported) {
            // Valid Media ID, Supported Playback
            return (
              <Player
                ks={this.props.ks}
                mediaID={this.props.mediaID}
                fileId={this.getFileID()}
              />
            );
          } else {
            // Valid Media ID, Unsupported Playback
            return (
              <UnsupportedPlaybackMessage
                mediaTitle={this.state.mediaInfo.displayTitle}
              />
            );
          }
        } else {
          // Invalid Media ID
          return (
            <GenericPlayerMessage message={MESSAGE_MAP.INVALID_MEDIA_ID} />
          );
        }
      }
    };
    return (
      <div
        className={`player-container ${
          !document.fullscreenEnabled ? 'fullscreen-disabled' : ''
        }`}
      >
        {this.state.loaded && renderedContent()}
      </div>
    );
  }
}

export default PlayerContainer;
